// Empotage weighing math — pure helpers shared by the panel and the
// future rapport-d'empotage generator.
//
// Per the CADESA OT workshop, each container records five numeric inputs:
//   loadedKg, emptyKg, theoreticalTareKg, realTareKg, dressingKg
// and we derive two net weights from them:
//   netTheoretical = loadedKg - emptyKg - theoreticalTareKg - dressingKg
//   netReal        = loadedKg - emptyKg - realTareKg        - dressingKg
//
// `dressingKg` (tare habillage) is the wrapping weight; it's missing on
// historic rows where the field hadn't been collected yet, in which
// case it's treated as 0 so prior nets stay reproducible.
//
// The OT-level rollups are:
//   engagé   — Σ(lot.netKg) across attached lots — what was committed.
//   totalNet — Σ(container nets) across the OT.
//   écart    — engagé - totalNet (positive = under-shipped).
//   freinte  — écart / engagé × 100, expressed as a percentage.
//
// Legacy fallback: rows that only carry the old `weighingWeightKg` field
// still contribute via that single number, treated as the net for both
// theoretical and real rollups. This keeps historic OTs comparable.

import type { OTContainer, OTLotLink } from "@/features/transit/api/types"

export interface ContainerWeightInputs {
  loadedKg: number | null | undefined
  emptyKg: number | null | undefined
  theoreticalTareKg?: number | null | undefined
  realTareKg?: number | null | undefined
  dressingKg?: number | null | undefined
  weighingWeightKg?: number | null | undefined
}

function net(loaded: number, empty: number, tare: number, dressing: number): number {
  return Math.round(loaded - empty - tare - dressing)
}

function isNum(v: number | null | undefined): v is number {
  return typeof v === "number" && Number.isFinite(v)
}

// dressingKg is optional — historic rows have no value, so we coerce
// missing/non-numeric to 0 instead of poisoning the whole net into null.
function dressingOrZero(v: number | null | undefined): number {
  return isNum(v) ? v : 0
}

export function containerNetTheoretical(c: ContainerWeightInputs): number | null {
  if (isNum(c.loadedKg) && isNum(c.emptyKg) && isNum(c.theoreticalTareKg)) {
    return net(c.loadedKg, c.emptyKg, c.theoreticalTareKg, dressingOrZero(c.dressingKg))
  }
  return isNum(c.weighingWeightKg) ? Math.round(c.weighingWeightKg) : null
}

export function containerNetReal(c: ContainerWeightInputs): number | null {
  if (isNum(c.loadedKg) && isNum(c.emptyKg) && isNum(c.realTareKg)) {
    return net(c.loadedKg, c.emptyKg, c.realTareKg, dressingOrZero(c.dressingKg))
  }
  return isNum(c.weighingWeightKg) ? Math.round(c.weighingWeightKg) : null
}

// OT-level totals. Returns null when no container contributes any net
// weight — the UI distinguishes "no data" from "0 kg".
export function otTotalNetTheoretical(
  containers: OTContainer[],
): number | null {
  return sumNets(containers, containerNetTheoretical)
}

export function otTotalNetReal(containers: OTContainer[]): number | null {
  return sumNets(containers, containerNetReal)
}

function sumNets(
  containers: OTContainer[],
  pick: (c: ContainerWeightInputs) => number | null,
): number | null {
  let total = 0
  let any = false
  for (const c of containers) {
    const v = pick({
      loadedKg: c.loadedKg ?? null,
      emptyKg: c.emptyKg ?? null,
      theoreticalTareKg: c.theoreticalTareKg ?? null,
      realTareKg: c.realTareKg ?? null,
      dressingKg: c.dressingKg ?? null,
      weighingWeightKg: c.weighingWeightKg ?? null,
    })
    if (v == null) continue
    total += v
    any = true
  }
  return any ? total : null
}

// Total committed weight from attached lots (kg). Falls back to 0 when
// no lots have a recorded netKg — the panel renders "—" rather than
// dividing by zero.
export function lotsTotalKg(lots: OTLotLink[]): number {
  let total = 0
  for (const link of lots) {
    if (isNum(link.lot?.netKg)) total += link.lot.netKg
  }
  return total
}

// écart = engagé − totalNet. freintePct = écart / engagé × 100. Returns
// null when either input is unusable (no engagé or no totalNet).
export function ecartFreinte(
  engageKg: number,
  totalNetKg: number | null,
): { ecart: number; freintePct: number } | null {
  if (totalNetKg == null || engageKg <= 0) return null
  const ecart = engageKg - totalNetKg
  const freintePct = (ecart / engageKg) * 100
  return { ecart: Math.round(ecart), freintePct }
}
